import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Helmet } from "react-helmet-async";

const Apropos = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("mission");
  const [retryCount, setRetryCount] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000
      );

      const response = await apiClient.get("/apropos", {
        cancelToken: source.token,
      });

      clearTimeout(timeoutId);

      // Validate response structure
      if (Array.isArray(response.data) && response.data.length > 0) {
        setData(response.data);
      } else {
        throw new Error("Invalid data format received from server");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(new Error("Request timeout: Please check your connection"));
      } else if (err.response) {
        setError(
          new Error(
            `Server Error: ${err.response.status} - ${
              err.response.data?.message || "Unknown error"
            }`
          )
        );
      } else if (err.request) {
        setError(
          new Error(
            "Network Error: Unable to reach the server. Please check your connection."
          )
        );
      } else {
        setError(new Error(`Error: ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des informations...</p>
        </div>
      </div>
    );
  }

  // Show full page error
  if (error && data.length === 0) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement</h4>
                <p className="mb-3">{error.message}</p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>{" "}
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i> Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const title = data[0]?.titre_apropos || "À Propos de Nous";
  const description =
    data[0]?.description_apropos || "Description non disponible";
  const image = data[0]?.image_path || "images/others/default-image.jpg";

  // TAB DATA
  const tabs = [
    {
      id: "mission",
      label: "Notre mission",
      icon: "fas fa-rocket",
      content: (
        <p>
          Notre mission est d'avancer le progrès et l'avancement numérique en
          livrant des technologies de pointe et une éducation de haute qualité.
        </p>
      ),
    },
    {
      id: "vision",
      label: "Notre vision",
      icon: "far fa-eye",
      content: (
        <p>
          Inspirer la créativité et favoriser une culture de croissance, en
          façonnant un avenir où la technologie permet aux individus et aux
          organisations d'atteindre leur plein potentiel.
        </p>
      ),
    },
    {
      id: "comit",
      label: "Notre engagement",
      icon: "fas fa-hands-helping",
      content: (
        <p>
          Être votre partenaire de confiance pour rendre le progrès numérique
          accessible et porteur de sens, en alliant technologie de pointe et
          éducation d'excellence.
        </p>
      ),
    },
    {
      id: "value",
      label: "Valeurs fondamentales",
      icon: "fas fa-hand-peace",
      content: (
        <ul className="list-unstyled">
          <li className="media my-4">
            <i className="fas fa-handshake fa-2x mr-3"></i>
            <div className="media-body">
              <h5 className="mt-0 mb-1">Collaboration</h5>
              <p>
                Nous croyons en la force du travail en équipe et en la
                coopération pour atteindre des objectifs communs.
              </p>
            </div>
          </li>
          <li className="media my-4">
            <i className="fas fa-lightbulb fa-2x mr-3"></i>
            <div className="media-body">
              <h5 className="mt-0 mb-1">Innovation</h5>
              <p>
                Nous favorisons la créativité et l'expérimentation pour proposer
                des solutions nouvelles et efficaces.
              </p>
            </div>
          </li>
          <li className="media my-4">
            <i className="fas fa-trophy fa-2x mr-3"></i>
            <div className="media-body">
              <h5 className="mt-0 mb-1">Excellence</h5>
              <p>
                Nous nous engageons à atteindre le plus haut niveau de qualité
                et de performance dans tout ce que nous entreprenons.
              </p>
            </div>
          </li>
          <li className="media my-4">
            <i className="fas fa-headset fa-2x mr-3"></i>
            <div className="media-body">
              <h5 className="mt-0 mb-1">Orientation Client</h5>
              <p>
                La satisfaction de nos clients est au cœur de nos
                préoccupations, nous les écoutons et nous nous adaptons à leurs
                besoins.
              </p>
            </div>
          </li>
          <li className="media my-4">
            <i className="fas fa-balance-scale fa-2x mr-3"></i>
            <div className="media-body">
              <h5 className="mt-0 mb-1">Intégrité</h5>
              <p>
                Nous agissons toujours avec honnêteté, transparence et respect
                des valeurs éthiques.
              </p>
            </div>
          </li>
        </ul>
      ),
    },
  ];

  return (
    <div>
      {/* SEO with React Helmet */}
      <Helmet>
        <title>{`${title} | DVELGE Group`}</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content="DVELGE, transformation digitale, développement logiciel, formation IT, fournitures technologiques, Matica, Wavetec, OCOM"
        />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: title,
            description: description,
            image: image,
            url: "https://www.dvelgegroup.com/apropos",
            publisher: {
              "@type": "Organization",
              name: "DVELGE Group",
              logo: {
                "@type": "ImageObject",
                url: "https://www.dvelgegroup.com/images/logo.png",
              },
              sameAs: [
                "https://www.facebook.com/dvelgegroup",
                "https://www.linkedin.com/company/dvelgegroup",
                "https://www.instagram.com/dvelgegroup",
                "https://x.com/dvelgegroup",
                "https://tiktok.com/@dvelgegroup",
                "https://www.youtube.com/@dvelgegroup",
              ],
              department: [
                {
                  "@type": "Organization",
                  name: "Développement logiciel",
                  url: "https://logiciel.dvelgegroup.com",
                },
                {
                  "@type": "Organization",
                  name: "Formation & certification IT",
                  url: "https://formation.dvelgegroup.com",
                },
                {
                  "@type": "Organization",
                  name: "Fourniture & achats IT",
                  url: "https://fourniture.dvelgegroup.com",
                },
              ],
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "Service des ventes",
                  email: "ventes@dvelgegroup.com",
                  telephone: "+224 627 02 08 71",
                  areaServed: "GN",
                  availableLanguage: ["fr"],
                },
              ],
            },
          })}
        </script>
      </Helmet>

      {/* PAGE CONTENT */}
      <section className="page-heading-sec about-pag-head pt85 pb120">
        <div className="container">
          <div className="row pt80">
            <div className="col-lg-6">
              <div className="page-heading mr25">
                <span
                  className="sub-heading mb15"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  <i className="fas fa-building mr5"></i> DVELGE GROUP
                </span>
                <h1 data-aos="fade-up" data-aos-delay="400">
                  {title}
                </h1>
                <p
                  className="mt30 h-light"
                  data-aos="fade-up"
                  data-aos-delay="600"
                >
                  {description}
                </p>
                {error && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>{" "}
                      Données en cache: {error.message}
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={handleRetry}
                        disabled={loading}
                      >
                        {loading ? "Chargement..." : "Actualiser"}
                      </button>
                    </small>
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="sol-img m-mt30">
                <img
                  src={image}
                  alt="DVELGE Image"
                  className="img-fluid"
                  data-aos="fade-in"
                  data-aos-delay="400"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="r-bg-c sec-pad">
        <div className="container">
          <div className="row">
            <div className="col-lg-4">
              <div className="page-headings side-pghd">
                <span className="sub-heading mb15">À propos de nous</span>
                <h2>Qui sommes-nous ?</h2>
              </div>
            </div>
            {loading && retryCount > 0 ? (
              <div className="col-lg-8">
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <span className="ms-3">Mise à jour des données...</span>
                </div>
              </div>
            ) : (
              <div className="col-lg-8">
                <h4 className="mb15">{title}</h4>
                <p>{description}</p>
                <p className="mt15">
                  Pour en savoir plus sur notre approche du travail, n'hésitez
                  pas à consulter notre page de contact.
                </p>
                <div className="row justify-content-center">
                  <div className="col-lg-12">
                    <div className="company-stats2 roww mt30">
                      <div className="statsnum counter-number mt30">
                        <p>Projets complétés</p>
                        <span className="counter">10</span>
                        <span>+</span>
                      </div>
                      <div className="statsnum counter-number mt30">
                        <p>Clients satisfaits</p>
                        <span className="counter">12</span>
                        <span>+</span>
                      </div>
                      <div className="statsnum counter-number mt30">
                        <p>Équipe d'experts</p>
                        <span className="counter">17</span>
                        <span>+</span>
                      </div>
                      <div className="statsnum counter-number mt30">
                        <p>Années de succès</p>
                        <span className="counter">3</span>
                        <span>+</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* REACT TABS */}
      <section className="r-bg-a sec-pad">
        <div className="container">
          <div className="row">
            <div className="col-lg-4 col-sm-12">
              <ul
                className="vrt-tabb nav nav-pills nav-pills-custom"
                role="tablist"
                aria-orientation="vertical"
              >
                {tabs.map((tab) => (
                  <li key={tab.id}>
                    <button
                      className={`nav-link ${
                        activeTab === tab.id ? "active" : ""
                      }`}
                      onClick={() => setActiveTab(tab.id)}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                    >
                      <i className={`${tab.icon}`}></i> {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-8 col-sm-12">
              <div className="tab-content tab-bg">
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={`tab-pane fade ${
                      activeTab === tab.id ? "show active" : ""
                    }`}
                    role="tabpanel"
                  >
                    <h3 className="mb15">{tab.label}</h3>
                    {tab.content}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Apropos;
